import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { MapPin, Phone, Mail, Clock, Send, ExternalLink } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import { api, PROJECT_TYPES } from "../lib/config";
import { toast } from "sonner";
import useSEO from "../hooks/useSEO";
import FranchiseForm from "../components/FranchiseForm";

// Fix default marker icons for Leaflet
delete L.Icon.Default.prototype._getIconUrl;

const goldIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#9A8640;border:3px solid #DDD4B2;box-shadow:0 0 0 3px #171717;"></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const ContactPage = () => {
  useSEO({
    title: "Contact",
    description: "Contactez Passion Verre pour votre projet de gravure laser sur mesure : mariage, cadeaux personnalisés, cadeaux d'entreprise. Atelier à Moidieu (38), sur rendez-vous.",
  });
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], message: "", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    (async () => {
      const [m, c] = await Promise.all([api.get("/markers"), api.get("/content")]);
      setMarkers(m.data);
      setInfo(c.data.contact_info);
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Vous devez accepter le traitement de vos données pour envoyer.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Nous revenons vers vous rapidement.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], message: "", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi. Veuillez réessayer.");
    }
    setLoading(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const center = markers[0] ? [markers[0].lat, markers[0].lng] : [45.4806, 5.0461];

  return (
    <div data-testid="contact-page">
      <section className="pt-32 pb-12 bg-[#171717]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Ornament />
          <SectionOverline className="mt-6">Contact</SectionOverline>
          <h1 className="font-serif text-5xl sm:text-6xl text-white mt-4 mb-6">Parlons de votre projet</h1>
        </div>
      </section>

      <section className="py-16 bg-[#171717]">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          {/* FORM */}
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <SectionOverline>Formulaire</SectionOverline>
            <h2 className="font-serif text-3xl text-[#DDD4B2]">Écrivez-nous</h2>
            <div className="grid grid-cols-2 gap-5">
              <input required placeholder="Prénom" value={form.first_name} onChange={set("first_name")} className="input-elegant" data-testid="input-first-name" />
              <input required placeholder="Nom" value={form.last_name} onChange={set("last_name")} className="input-elegant" data-testid="input-last-name" />
            </div>
            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="input-elegant" data-testid="input-email" />
            <input placeholder="Téléphone" value={form.phone} onChange={set("phone")} className="input-elegant" data-testid="input-phone" />
            <select required value={form.project_type} onChange={set("project_type")} className="input-elegant bg-[#171717]" data-testid="input-project-type">
              {PROJECT_TYPES.map((t) => <option key={t} value={t} className="bg-[#171717]">{t}</option>)}
            </select>
            <textarea required rows={5} placeholder="Votre message" value={form.message} onChange={set("message")} className="input-elegant resize-none" data-testid="input-message" />
            <label className="flex items-start gap-3 text-xs text-[#DDD4B2]/85 cursor-pointer" data-testid="consent-label">
              <input
                type="checkbox"
                required
                checked={form.consent}
                onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                className="mt-1 accent-[#9A8640] w-4 h-4 shrink-0"
                data-testid="input-consent"
              />
              <span className="leading-relaxed">
                J'accepte que Passion Verre traite les données saisies dans ce formulaire pour répondre à ma demande.
                Ces informations ne seront jamais transmises à des tiers. Pour en savoir plus, consultez notre{" "}
                <a href="/politique-confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#9A8640] underline hover:text-[#DDD4B2]">
                  Politique de confidentialité
                </a>.
              </span>
            </label>
            <button type="submit" disabled={loading || !form.consent} className="btn-gold px-8 py-4 text-sm uppercase inline-flex items-center gap-3 disabled:opacity-60" data-testid="contact-submit">
              {loading ? "Envoi…" : <>Envoyer <Send size={14} /></>}
            </button>
          </form>

          {/* INFO */}
          <div className="space-y-8">
            <div>
              <SectionOverline>Informations</SectionOverline>
              <h2 className="font-serif text-3xl text-[#DDD4B2] mt-3 mb-6">Où nous trouver</h2>
              <ul className="space-y-5 text-[#DDD4B2]/85">
                <li className="flex items-start gap-3"><MapPin className="text-[#9A8640] mt-1 shrink-0" size={18} /> {info?.address || "Moidieu-Détourbe, 38440"}</li>
                <li className="flex items-center gap-3"><Phone className="text-[#9A8640] shrink-0" size={18} /> {info?.phone}</li>
                <li className="flex items-center gap-3"><Mail className="text-[#9A8640] shrink-0" size={18} /> {info?.email}</li>
                <li className="flex items-start gap-3"><Clock className="text-[#9A8640] mt-1 shrink-0" size={18} /> {info?.hours}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#171717]">
        <div className="max-w-7xl mx-auto px-6 pb-8">
          <div className="text-center mb-10">
            <SectionOverline>Notre carte</SectionOverline>
            <h2 className="font-serif text-4xl text-[#DDD4B2] mt-3">L'atelier et nos partenaires</h2>
          </div>
          <div className="h-[500px] border border-[#9A8640]/30 leaflet-container-dark" data-testid="map-container">
            <MapContainer center={center} zoom={10} className="h-full w-full">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
              {markers.map((m) => (
                <Marker key={m.id} position={[m.lat, m.lng]} icon={goldIcon}>
                  <Popup>
                    <div>
                      <strong style={{ color: "#9A8640", fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}>{m.name}</strong>
                      <div style={{ marginTop: 4, fontSize: 12, color: "#DDD4B2" }}>{m.address}</div>
                      {m.products && <div style={{ marginTop: 6, fontSize: 12, color: "#DDD4B2" }}>{m.products}</div>}
                      {m.website && (
                        <a href={m.website} target="_blank" rel="noopener noreferrer" style={{ marginTop: 6, display: "inline-flex", gap: 4, alignItems: "center", fontSize: 12 }}>
                          Site web <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* FRANCHISE */}
        <FranchiseForm />
      </section>
    </div>
  );
};

export default ContactPage;
